import { motion } from "framer-motion";
import { Phone, MapPin, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { validateContactNumber } from "../../../utils/formValidators";

export default function Step3ContactDetails({ formData, setFormData, errors, compact = false }) {
  const [phoneValidation, setPhoneValidation] = useState({ contactNumber: null });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (field, value) => {
    handleChange(field, value);
    const validation = validateContactNumber(value);
    setPhoneValidation((prev) => ({
      ...prev,
      [field]: validation.isValid ? "valid" : null,
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className={`rounded-xl border border-gray-200 bg-white shadow-sm ${compact ? "p-4 sm:p-5" : "p-6 sm:p-8"}`}
    >
      {!compact && (
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-md">
            <Phone size={20} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Contact Details</h2>
        </div>
      )}

      {compact && (
        <p className="mb-4 text-xs text-gray-600">
          Required for follow-up and travel communication.
        </p>
      )}

      <div className={`grid grid-cols-1 gap-5 ${compact ? "lg:grid-cols-2" : "md:grid-cols-2"}`}>
        <div>
          <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
            <Phone size={16} className="text-orange-600" />
            Contact number (include country code) <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="tel"
              value={formData.contactNumber || ""}
              onChange={(e) => handlePhoneChange("contactNumber", e.target.value)}
              className={`w-full px-4 py-3 pr-10 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors?.contactNumber
                  ? "border-red-300 bg-red-50"
                  : phoneValidation.contactNumber === "valid"
                  ? "border-green-300 bg-green-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              placeholder="e.g. 233241234567 or 14155552671"
            />
            {phoneValidation.contactNumber === "valid" && (
              <CheckCircle2 className="absolute top-3.5 right-3 text-green-500" size={20} />
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Enter digits only (10 to 15) and do not include a leading +.
          </p>
          {errors?.contactNumber && (
            <p className="mt-1 text-sm text-red-500">{errors.contactNumber}</p>
          )}
        </div>

        <div>
          <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
            <MapPin size={16} className="text-orange-600" />
            Address <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.address || ""}
            onChange={(e) => handleChange("address", e.target.value)}
            className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
              errors?.address
                ? "border-red-300 bg-red-50"
                : "border-gray-300 hover:border-gray-400"
            }`}
            placeholder="Enter your address"
          />
          {errors?.address && (
            <p className="mt-1 text-sm text-red-500">{errors.address}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

